import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import PageHeader, { btnPrimary, btnSecondary, inputClass, cardClass } from '../components/PageHeader';
import SearchableSelect from '../components/SearchableSelect';
import { accountTypeOptions } from '../utils/statusConstants';

const empty = {
  account_type: 'residential',
  name: '',
  billing_email: '',
  phone: '',
  is_active: true,
};

const AccountForm = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) return;
    apiFetch(`/admin/accounts/${id}`)
      .then((r) => {
        const a = r.data;
        setForm({
          account_type: a.account_type,
          name: a.name,
          billing_email: a.billing_email || '',
          phone: a.phone || '',
          is_active: a.is_active,
        });
      })
      .catch(() => toast.error('Failed to load account'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await apiFetch(`/admin/accounts/${id}`, {
          method: 'PUT',
          body: JSON.stringify(form),
        });
        toast.success('Account updated');
      } else {
        await apiFetch('/admin/accounts', {
          method: 'POST',
          body: JSON.stringify(form),
        });
        toast.success('Account created');
      }
      navigate('/accounts');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    }
  };

  if (loading) return <p className="text-slate-500">Loading…</p>;

  return (
    <div className="max-w-xl">
      <PageHeader title={isEdit ? 'Edit account' : 'New account'} />
      <Link to="/accounts" className="text-sm text-teal-700 mb-4 inline-block">
        ← Accounts
      </Link>
      <form onSubmit={submit} className={`${cardClass} p-6 space-y-4`}>
        <SearchableSelect
          label="Type"
          value={form.account_type}
          onChange={(account_type) => setForm({ ...form, account_type })}
          options={accountTypeOptions}
          placeholder="Select account type…"
          required
        />
        <div>
          <label className="block text-sm font-medium mb-1">Name *</label>
          <input
            className={inputClass}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Billing email</label>
          <input
            type="email"
            className={inputClass}
            value={form.billing_email}
            onChange={(e) => setForm({ ...form, billing_email: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Phone</label>
          <input
            className={inputClass}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            Active
          </label>
        )}
        <div className="flex gap-3 pt-2">
          <button type="submit" className={btnPrimary}>
            Save
          </button>
          <Link to="/accounts" className={btnSecondary}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
};

export default AccountForm;
